import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { useContext, useRef, useState } from "react";
import { FormattedMessage } from "react-intl";
import styles from "./SocketConversationSelector.module.less";
import { CopilotMessageContext } from "~/providers/CopilotMessageProvider";
import { COPOLIT_SOCKET_KEY } from "~/constants/socket";
import { STEEL_SOCKET_URL } from "~/constants/url";
import { ConvertRequestSocketModelToViewModel } from "~/helpers/mapper";
import { useAddSocketListener } from "~/hooks/useAddSocketListener";
import { forceReloadConversations } from "../../hooks/useGetConversations";
import { reloadRequests } from "../../hooks/useGetRequests";
import { RequestResponseSocketModel } from "../../types/copilot";
import moment from "moment";
import { mutateCampaignHeatsForAsset } from "../../hooks/useGetCampaignHeatsForAsset";
import { useRequestsByIds } from "../../hooks/useRequestsByIds";

const COUNTDOWN_TIME = 30;

let refreshTimeout: NodeJS.Timeout | undefined;
let countDownTimer: NodeJS.Timeout | undefined;

let lastConversationId: string | undefined;

const clearTimers = () => {
  if (refreshTimeout != undefined) clearTimeout(refreshTimeout);

  if (countDownTimer != undefined) clearInterval(countDownTimer);
};

function SocketConversationSelector() {
  const {
    setSelectedConversation,
    setFilteredLadleIdList,
    conversationList,
    selectedConversationRef,
  } = useContext(CopilotMessageContext);

  const { getRequestsByIds } = useRequestsByIds();

  const [countDown, setCountDown] = useState(0);
  const countDownRef = useRef(countDown);
  countDownRef.current = countDown;

  const conversationListRef = useRef(conversationList);
  conversationListRef.current = conversationList;

  const onConversationRequestUpdated = () => {
    forceReloadConversations();
  };

  const changeSelectedConversation = () => {
    setCountDown(0);
    setSelectedConversation(
      conversationListRef.current?.find((x) => x.id == lastConversationId),
    );
    setFilteredLadleIdList(undefined);
  };

  const onRequestsUpdated = async (message: string) => {
    const parsedMessage = JSON.parse(message) as RequestResponseSocketModel[];

    const { requests, responses } =
      ConvertRequestSocketModelToViewModel(parsedMessage);

    if (selectedConversationRef.current === undefined) return;

    const isSelectedConversation = requests.some(
      (x) => x.conversationId === selectedConversationRef.current!.id,
    );

    if (isSelectedConversation) {
      //Yeni gelen verinin tarihine bakmaksızın en alta koyuyoruz.
      const filteredRequestsByConversation = requests.filter(
        (x) => x.conversationId === selectedConversationRef.current!.id,
      );

      const currentConversation = conversationListRef.current?.find(
        (x) => x.id === selectedConversationRef.current!.id,
      );

      if (currentConversation == undefined) return;

      //#region #14562

      mutateCampaignHeatsForAsset(currentConversation?.assetId ?? "");

      const isAllOlderThanCurrentConversation = responses.every((r) =>
        moment(r.operationDate).isBefore(
          moment(currentConversation?.latestResponseOperationDate),
        ),
      );
      //#endregion #14562

      if (isAllOlderThanCurrentConversation) return;

      const newRequestsIds = filteredRequestsByConversation
        .filter((x) =>
          moment(x.operationDate).isAfter(
            moment(currentConversation?.latestResponseOperationDate),
          ),
        )
        .map((x) => x.id);
      const newRequestResponseResult = await getRequestsByIds(newRequestsIds);

      if (
        newRequestResponseResult?.requests &&
        newRequestResponseResult?.responses
      ) {
        const newData = {
          requests: newRequestResponseResult?.requests,
          responses: newRequestResponseResult?.responses.filter((x) =>
            filteredRequestsByConversation.some((fr) => fr.id === x.requestId),
          ),
        };
        reloadRequests(selectedConversationRef.current.id, newData);
      }
    } else {
      const lastSocketConvervation = requests.sort((a, b) =>
        moment(b.operationDate).diff(moment(a.operationDate)),
      )[0];

      if (lastSocketConvervation == undefined) return;

      const lastConversation = conversationListRef.current?.find(
        (t) => t.id == lastSocketConvervation.conversationId,
      );

      //#region #14562
      const isOlderData = moment(lastSocketConvervation.operationDate).isBefore(
        moment(lastConversation?.latestResponseOperationDate),
      );

      if (isOlderData) return;

      //#endregion #14562

      lastConversationId = lastSocketConvervation.conversationId;

      const selectedConversationLastOperationDate =
        conversationListRef.current?.find(
          (x) => x.id == lastConversationId,
        )?.latestResponseOperationDate;

      const isNewestData = selectedConversationLastOperationDate
        ? lastSocketConvervation.operationDate >
          selectedConversationLastOperationDate
        : true;

      if (isNewestData) {
        startCountdown();
        refreshTimeout = setTimeout(() => {
          changeSelectedConversation();
        }, COUNTDOWN_TIME * 1000);
      }
    }
    // reloadConversations(
    //   requests.map((x) => ({
    //     id: x.conversationId,
    //     latestResponseOperationDate: x.operationDate,
    //   }))
    // );
    forceReloadConversations();
  };

  useAddSocketListener(STEEL_SOCKET_URL, COPOLIT_SOCKET_KEY, {
    OnCopilotConversationsUpdated: onConversationRequestUpdated,
    OnCopilotRequestsUpdated: onRequestsUpdated,
  });

  const startCountdown = () => {
    if (countDownRef.current > 0) return;

    setCountDown(COUNTDOWN_TIME);
    clearTimers();

    countDownTimer = setInterval(() => {
      setCountDown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
  };

  return countDown > 0 ? (
    <div className={styles.pageReshreshContainer}>
      <div className={styles.title}>
        <ExclamationCircleOutlined style={{ color: "#e74c3c" }} />
        <FormattedMessage id="ConversationDetail.Warning.SelectionChange" />
      </div>

      <div className={styles.message}>
        {`New data arrived. Selected ladle will be changed in ${countDown} second${
          countDown == 1 ? "" : "s"
        }`}
      </div>

      <div className={styles.buttons}>
        <Button.Group>
          <Button
            onClick={() => {
              clearTimers();
              setCountDown(0);
            }}
          >
            <FormattedMessage id="General.Label.LowerCaseCancel" />
          </Button>
          <Button
            onClick={() => {
              changeSelectedConversation();
            }}
          >
            <FormattedMessage id="General.Label.Continue" />
          </Button>
        </Button.Group>
      </div>
    </div>
  ) : null;
}

export default SocketConversationSelector;
